import React, { useEffect, useRef, useState } from 'react';
import { INTER_TYPES_QUICK, TEMP_LEVELS } from '../lib/constantes';
import { getTempLevel } from '../lib/formato';

function TempPicker({ current, onChange }) {
  const [open, setOpen] = React.useState(false);
  const t = getTempLevel(current);

  return <div className="relative" onClick={e=>e.stopPropagation()}>
    <button
      onClick={e=>{ e.stopPropagation(); setOpen(o=>!o); }}
      title={t ? `${t.emoji} ${t.label} — clique para alterar` : 'Definir temperatura'}
      className={`flex items-center gap-1 px-2 py-0.5 rounded-full t-caption-2 font-semibold border transition-all hover:opacity-80 ${
        t ? `${t.bg} ${t.text} ${t.border}` : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200'
      }`}
    >
      {t ? <>{t.emoji} {t.label}</> : <span className="opacity-60">temp.</span>}
    </button>

    {open && <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-2xl shadow-xl p-2 flex flex-col gap-1 min-w-[140px] animate-fade-in-up">
      {TEMP_LEVELS.map(lv=>(
        <button key={lv.v}
          onClick={e=>{ e.stopPropagation(); onChange(lv.v); setOpen(false); }}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
            current===lv.v ? `${lv.bg} ${lv.text} ${lv.border} border` : 'hover:bg-gray-50 text-gray-700'
          }`}
        >
          {lv.emoji} {lv.label}
        </button>
      ))}
      {current && <button
        onClick={e=>{ e.stopPropagation(); onChange(null); setOpen(false); }}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs text-gray-400 hover:bg-gray-50 transition-all"
      >✕ Remover</button>}
    </div>}
  </div>;
}

function SnoozePicker({ onSnooze, onDismiss, current = null }) {
  // `current` é a cadência já gravada no lead; abre com o toggle ligado e o intervalo destacado.
  const [repeat, setRepeat] = React.useState(!!current);
  return <div className="mt-1.5 bg-white border border-blue-100 rounded-xl p-2 shadow-md" onClick={e=>e.stopPropagation()}>
    <p className="t-caption-2 text-gray-400 font-semibold mb-1.5 px-0.5">Lembrar novamente em:</p>
    <div className="flex gap-1.5 mb-1.5">
      {[3,7,15,30].map(d=><button key={d} type="button"
        onClick={e=>{e.stopPropagation();onSnooze(d, repeat);}}
        className={`flex-1 text-xs py-1.5 rounded-lg font-bold transition-colors ${current===d ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
        {d}d
      </button>)}
    </div>
    <button type="button" aria-pressed={repeat}
      onClick={e=>{e.stopPropagation();setRepeat(r=>!r);}}
      title={repeat ? 'Reagenda sozinho a cada contato feito' : 'Só desta vez'}
      className={`w-full flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-lg mb-1.5 font-semibold border transition-colors ${repeat ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
      <span aria-hidden="true">🔁</span> {repeat ? 'Repetindo sempre' : 'Repetir sempre'}
    </button>
    <button type="button" onClick={e=>{e.stopPropagation();onDismiss();}}
      className="w-full text-xs py-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 font-medium transition-colors">
      {current ? 'Encerrar ciclo (sem reagendar)' : 'Dispensar (sem reagendar)'}
    </button>
  </div>;
}

function QuickStatusPopup({ leadId, onSave, onClose }) {
  const [type, setType] = React.useState('whatsapp');
  const [note, setNote] = React.useState('');
  const [date, setDate] = React.useState(new Date().toISOString().slice(0,10));
  const [saving, setSaving] = React.useState(false);
  const ref = React.useRef(null);

  // Fechar ao clicar fora
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fechar com Escape
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!note.trim()) return;
    setSaving(true);
    await onSave(leadId, { type, note: note.trim(), date });
    setSaving(false);
    onClose();
  };

  const inp = 'w-full px-2.5 py-1.5 text-xs rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all';

  return (
    <div ref={ref}
      onClick={e => e.stopPropagation()}
      className="absolute bottom-full left-0 right-0 mb-1.5 z-40 bg-white border border-gray-200 rounded-2xl shadow-xl p-3 animate-fade-in-up"
      style={{minWidth:'220px'}}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="t-caption-2 font-bold text-gray-600 sf-caption">Registrar interação</span>
        <button onClick={e=>{e.stopPropagation();onClose();}} className="text-gray-400 hover:text-gray-600 text-xs leading-none">✕</button>
      </div>

      {/* Tipo — botões compactos */}
      <div className="flex gap-1 mb-2 flex-wrap">
        {INTER_TYPES_QUICK.map(t => (
          <button key={t.key} type="button"
            onClick={e=>{e.stopPropagation();setType(t.key);}}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg t-caption-2 font-semibold transition-all ${
              type===t.key ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Nota */}
      <textarea
        className={inp + ' resize-none mb-2'}
        rows={2}
        placeholder="Resumo da interação..."
        value={note}
        onChange={e => setNote(e.target.value)}
        onKeyDown={e => { if(e.key==='Enter' && (e.metaKey||e.ctrlKey)) handleSave(e); }}
        autoFocus
      />

      {/* Data + salvar */}
      <div className="flex gap-1.5 items-center">
        <input type="date" className={inp + ' flex-1'} value={date} onChange={e=>setDate(e.target.value)} />
        <button
          onClick={handleSave}
          disabled={saving || !note.trim()}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition-all disabled:opacity-40 flex-shrink-0"
        >
          {saving ? '...' : 'Salvar'}
        </button>
      </div>
      <p className="t-caption-2 text-gray-400 mt-1.5">⌘+Enter para salvar</p>
    </div>
  );
}

export { QuickStatusPopup, SnoozePicker, TempPicker };
